import { useState } from 'react';

import { Button } from '../../../../components/Button';
import { Modal } from '../../../../components/Modal';
import { Tooltip } from '../../../../components/Tooltip';
import { InlineAddTable, InlineRow } from '../../../../components/inline-add-table/InlineAddTable';
import { TdInput, TdNumber } from '../../../../components/inline-add-table/cells';
import { FormFieldset } from '../../../../components/inputs/FormFieldset';
import { TextField } from '../../../../components/inputs/TextField';
import { useToast } from '../../../../components/toastContext';
import { ApiError } from '../../../../lib/apiError';
import { uid } from '../../../../lib/uid';
import { focusValidationError } from '../../../../lib/validationFocus';
import { awardCategoriesApi } from '../praiseApi';
import type { AwardCategoryDetail } from '../praiseTypes';
import { sampleAwardCategory } from '../utils/samplePraise';

const ID_PREFIX = 'award-category';

/** `awardCategoryId` to `award-category-id` — exactly as `focusValidationError` does it. */
const kebab = (field: string) => field.replace(/([a-z0-9])([A-Z])/g, '$1-$2').toLowerCase();

/** The id of one inline cell — a CONTRACT with the server, which names a rejected line `documents.2.name`. */
const cellId = (index: number, field: string) => `${ID_PREFIX}-documents.${index}.${kebab(field)}`;

interface DraftLine {
  key: string;
  /** The line's own id when it is already on the checklist — what keeps registered nominees pointing at it. */
  id: string | null;
  name: string;
  description: string;
  /** The points a new award starts this document at; null leaves the award form to ask. */
  defaultMaxPoints: number | null;
}

const EMPTY = (): DraftLine => ({ key: uid(), id: null, name: '', description: '', defaultMaxPoints: null });

/** A row nobody touched — the server's `IsBlank`. */
const isBlank = (line: DraftLine) =>
  line.name.trim() === '' && line.description.trim() === '' && line.defaultMaxPoints == null;

const isFilled = (line: DraftLine) => line.name.trim() !== '';

const linesOf = (editing: AwardCategoryDetail | null): DraftLine[] =>
  editing != null && editing.documents.length > 0
    ? editing.documents.map((d) => ({
        key: uid(),
        id: d.id,
        name: d.name,
        description: d.description ?? '',
        defaultMaxPoints: d.defaultMaxPoints ?? null,
      }))
    : [EMPTY()];

/** What the modal writes: a CREATE writes both; on a record each section has its own edit and write. */
export type AwardCategorySection = 'create' | 'details' | 'checklist';

/**
 * Create a PRAISE award category, or edit one section of it.
 *
 * **The checklist is the documents every nominee under the category submits.** A line already on
 * the checklist keeps its id, so the nominees registered against it keep pointing at it; one removed
 * here is no longer asked of new nominees and stays on the ones already registered.
 */
export function AwardCategoryModal({
  open,
  section,
  editing,
  onClose,
  onSaved,
}: {
  open: boolean;
  section: AwardCategorySection;
  /** The record a section edit works on. Null on a create. */
  editing: AwardCategoryDetail | null;
  onClose: () => void;
  /** `keepOpen` after a create with Add another on — the parent refreshes and leaves the form up. */
  onSaved: (saved: AwardCategoryDetail, options?: { keepOpen?: boolean }) => void;
}) {
  const toast = useToast();
  const creating = section === 'create';
  const showDetails = section !== 'checklist';
  const showChecklist = section !== 'details';

  const [name, setName] = useState(editing?.name ?? '');
  const [description, setDescription] = useState(editing?.description ?? '');
  const [lines, setLines] = useState<DraftLine[]>(() => linesOf(editing));

  const [addAnother, setAddAnother] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);

  const filledCount = lines.filter(isFilled).length;
  const linesValid = filledCount > 0 && lines.every((l) => isFilled(l) || isBlank(l));
  const valid = (!showDetails || name.trim().length > 0) && (!showChecklist || linesValid);
  const canSave = valid && confirmed && !saving;

  const saveDisabledReason = saving
    ? null
    : showDetails && name.trim() === ''
      ? 'Name the category.'
      : showChecklist && filledCount === 0
        ? 'Add at least one document to the checklist.'
        : showChecklist && !linesValid
          ? 'Every document needs a name. Remove the unfinished one, or fill it in.'
          : !confirmed
            ? 'Tick the confirm box to submit.'
            : null;

  function patch(key: string, change: Partial<DraftLine>) {
    setLines((current) => current.map((line) => (line.key === key ? { ...line, ...change } : line)));
  }

  // EVERY row is sent, blanks included, so a rejection's index is the row on screen.
  const documentsBody = () =>
    lines.map((l) => ({
      id: l.id,
      name: l.name.trim(),
      description: l.description.trim() || null,
      defaultMaxPoints: l.defaultMaxPoints,
    }));

  async function save() {
    if (!canSave) return;

    setSaving(true);
    setError(null);

    try {
      const saved =
        section === 'create'
          ? await awardCategoriesApi.create({
              name: name.trim(),
              description: description.trim() || null,
              documents: documentsBody(),
            })
          : section === 'details'
            ? await awardCategoriesApi.update(editing!.id, { name: name.trim(), description: description.trim() || null })
            : await awardCategoriesApi.updateChecklist(editing!.id, { documents: documentsBody() });

      toast.success(
        section === 'create'
          ? `${saved.name} was created.`
          : section === 'details'
            ? `${saved.name} was updated.`
            : `${saved.name}'s checklist was updated.`,
      );

      if (creating && addAnother) {
        setName('');
        setDescription('');
        setLines([EMPTY()]);
        setConfirmed(false);
        onSaved(saved, { keepOpen: true });
      } else {
        onSaved(saved);
      }
    } catch (caught) {
      const failure = caught instanceof ApiError ? caught : new ApiError('Could not save this category.', 0);

      setError(failure);
      focusValidationError(failure, {
        idPrefix: ID_PREFIX,
        // The refusals naming the WHOLE checklist — none at all, the cap — are answered at the `+`.
        fieldIds: { documents: `${ID_PREFIX}-add-document` },
      });
    } finally {
      setSaving(false);
    }
  }

  function generate() {
    const sample = sampleAwardCategory();

    // A section edit fills its own fields only, and never renames a record the reader opened.
    if (creating) setName(sample.name);
    if (showDetails) setDescription(sample.description);
    if (showChecklist) {
      setLines(
        sample.documents.map((d) => ({
          key: uid(),
          id: null,
          name: d.name,
          description: d.description,
          defaultMaxPoints: d.defaultMaxPoints,
        })),
      );
    }
  }

  const title =
    section === 'create' ? 'New award category' : section === 'details' ? `Edit ${editing!.name}` : `Edit checklist — ${editing!.name}`;

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={title}
      size={showChecklist ? '2xl' : 'lg'}
      id={`${ID_PREFIX}-modal`}
      autogenerate={{ onGenerate: generate, disabled: saving }}
      addAnother={
        creating ? { id: `${ID_PREFIX}-add-another`, checked: addAnother, onChange: setAddAnother, disabled: saving } : undefined
      }
      confirm={{ id: `${ID_PREFIX}-confirm`, checked: confirmed, onChange: setConfirmed, disabled: saving }}
      footer={
        <>
          <Button id={`${ID_PREFIX}-cancel`} variant="secondary" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Tooltip disabledReason={saveDisabledReason}>
            <Button
              id={`${ID_PREFIX}-submit`}
              variant="primary"
              onClick={() => void save()}
              disabled={!canSave}
              loading={saving}
              loadingLabel="Submitting"
            >
              Submit
            </Button>
          </Tooltip>
        </>
      }
    >
      <div className="flex flex-col gap-4">
        {error ? (
          <div role="alert" className="rounded-lg border border-red-200 bg-red-50 p-3">
            <p className="text-xs text-red-700">{error.message}</p>
          </div>
        ) : null}

        {showDetails ? (
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              id={`${ID_PREFIX}-name`}
              label="Name"
              required
              description="The kind of award — Outstanding Employee Award, Innovation Award."
              value={name}
              onChange={(e) => setName(e.target.value)}
              error={name.trim() ? error?.fieldError('name') : 'Name the category.'}
              disabled={saving}
            />

            <TextField
              id={`${ID_PREFIX}-description`}
              label="Description"
              description="One line saying what the award recognises. Shown to whoever opens an award under it."
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              error={error?.fieldError('description')}
              disabled={saving}
            />
          </div>
        ) : null}

        {showChecklist ? (
          <>
            <FormFieldset legend="Document requirements">
              <InlineAddTable
                headers={['Document *', { label: 'Default points', align: 'right' }, 'Description']}
                colWidths={[260, 140, 320]}
                resizeKey={`${ID_PREFIX}-documents-v2`}
                flexColumn={2}
                addButtonId={`${ID_PREFIX}-add-document`}
                addDisabled={saving || lines.some((l) => !isFilled(l))}
                addDisabledReason="Finish the current document before adding another."
                rowCount={lines.length}
                onAddRow={() => setLines((current) => [...current, EMPTY()])}
              >
                {lines.map((line, index) => (
                  <InlineRow
                    key={line.key}
                    idx={index + 1}
                    onRemove={() => setLines((current) => current.filter((l) => l.key !== line.key))}
                  >
                    <TdInput
                      id={cellId(index, 'name')}
                      ariaLabel={`Document, line ${index + 1}`}
                      value={line.name}
                      onChange={(next) => patch(line.key, { name: next })}
                      placeholder="Nomination form"
                      disabled={saving}
                    />
                    <TdNumber
                      id={cellId(index, 'defaultMaxPoints')}
                      ariaLabel={`Default points, line ${index + 1}`}
                      value={line.defaultMaxPoints}
                      onChange={(next) => patch(line.key, { defaultMaxPoints: next })}
                      allowNull
                      integerOnly
                      min={0}
                      disabled={saving}
                    />
                    <TdInput
                      id={cellId(index, 'description')}
                      ariaLabel={`Description, line ${index + 1}`}
                      value={line.description}
                      onChange={(next) => patch(line.key, { description: next })}
                      disabled={saving}
                    />
                  </InlineRow>
                ))}
              </InlineAddTable>

              {error?.fieldError('documents') ? <p className="text-xs text-red-700">{error.fieldError('documents')}</p> : null}
            </FormFieldset>

            {/* The CONSEQUENCE, read before the tick: the checklist is copied onto a nominee when registered. */}
            <p className="text-xs text-slate-500">
              A nominee for an award under this category is asked for every document on the checklist as it stands when they
              are nominated. Removing a document leaves it on the nominees already registered. Each document is also a
              scoring criterion; its default points are what a new award starts it at. When someone who can also change
              these categories saves an award's points, they become the new default here; awards already created keep
              their own.
            </p>
          </>
        ) : null}
      </div>
    </Modal>
  );
}
